import type { Currency, Pillar, Target, TargetOverlapConflict, Unit, UserRole, Week } from 'chery-contracts';
import { BLANK_DISPLAY, formatValue, isNumericUnit, PILLARS, targetKpiCompatible } from 'chery-kpi-engine';
import type { KpiRow } from '../kpis/kpi-display';
import { weekDateLabel } from '../tracker/tracker-display';

/**
 * Presentation rules of the Targets setup (plan §3.1, §7) — pure and unit-tested. The service owns every rule; these
 * only keep the form from offering what it would refuse (a KPI of another unit or currency).
 */

/** Targets are written by ADMIN and the PM team; everybody else reads them. */
export const canManageTargets = (role: UserRole | undefined): boolean => role === 'ADMIN' || role === 'PM';

/** `at least` / `at most`. */
export const directionText = (direction: Target['direction']): string => (direction === 'AT_LEAST' ? 'at least' : 'at most');

/** `800.0`, `JOD 20,000,000.0`. */
export const targetValueText = (target: Pick<Target, 'value' | 'unit' | 'currency'>): string =>
  isNumericUnit(target.unit) ? formatValue(target.unit, target.value, target.currency) : BLANK_DISPLAY;

/** `2026` for an annual target; `4 Jan 2026 → 29 Mar 2026` for a custom one. */
export const periodText = (target: Pick<Target, 'periodType' | 'year' | 'fromWeekDate' | 'toWeekDate'>): string =>
  target.periodType === 'ANNUAL' && target.year !== null
    ? String(target.year)
    : `${weekDateLabel(target.fromWeekDate)} → ${weekDateLabel(target.toWeekDate)}`;

/** KPIs a target of `unit` (and `currency`) may link to, grouped by pillar in the fixed order; never Text. */
export const compatibleKpisByPillar = (rows: readonly KpiRow[], unit: Unit, currency: Currency | null): { pillar: Pillar; kpis: KpiRow[] }[] =>
  PILLARS.map((pillar) => ({
    pillar,
    kpis: rows.filter((row) => row.pillar === pillar && targetKpiCompatible({ unit, currency }, row)),
  })).filter((group) => group.kpis.length > 0);

/** The selected KPI ids that stay compatible after the unit or currency changed. */
export const keepCompatible = (selected: readonly string[], rows: readonly KpiRow[], unit: Unit, currency: Currency | null): string[] =>
  selected.filter((id) => {
    const row = rows.find((each) => each.id === id);

    return row !== undefined && targetKpiCompatible({ unit, currency }, row);
  });

/** Calendar years that have reporting weeks, newest last. */
export const yearOptions = (weeks: readonly Pick<Week, 'weekDate'>[]): number[] =>
  [...new Set(weeks.map((week) => Number(week.weekDate.slice(0, 4))))].sort();

/** The structured conflict of a 409 overlap answer, or `null` when the body is anything else. */
export const overlapConflictFrom = (data: unknown): TargetOverlapConflict['conflict'] | null => {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const { conflict } = data as { conflict?: unknown };

  return typeof conflict === 'object' && conflict !== null && typeof (conflict as { targetName?: unknown }).targetName === 'string'
    ? (conflict as TargetOverlapConflict['conflict'])
    : null;
};

/** Case-insensitive match on the target name and its linked KPIs' names and codes. */
export const matchesTargetSearch = (target: Pick<Target, 'name' | 'kpis'>, search: string): boolean => {
  const query = search.trim().toLowerCase();

  return (
    query === '' ||
    target.name.toLowerCase().includes(query) ||
    target.kpis.some((kpi) => kpi.name.toLowerCase().includes(query) || kpi.code.toLowerCase().includes(query))
  );
};
